import React, { useState, useMemo, memo } from 'react';
import PropTypes from 'prop-types';

const NeumorphicButtonLong = ({
  children,
  type = 'button',
  onClick,
  disabled,
  isLoading,
  className = '',
  textColor = '#9C9DA7',
  style = {} // Added style prop
}) => {
  // Generate unique IDs for filters and gradients using useMemo
  const filterIds = useMemo(() => ({
    filterId: `button_filter_${Math.random().toString(36).substr(2, 9)}`,
    gradientId: `button_gradient_${Math.random().toString(36).substr(2, 9)}`,
    hoverGradientId: `hover_gradient_${Math.random().toString(36).substr(2, 9)}`
  }), []);

  const { filterId, gradientId, hoverGradientId } = filterIds;

  // State for hover
  const [isHovered, setIsHovered] = useState(false);
  
  return (
    <div 
      className={`neumorphic-button-container ${className}`} 
      style={{ 
        position: 'relative', 
        width: '100%', 
        height: '88px',
        ...style // Allow style overrides
      }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <svg 
        style={{ 
          position: 'absolute', 
          width: '100%', 
          height: '100%', 
          top: 0, 
          left: 0,
          transition: 'all 0.3s ease'
        }}
        viewBox="0 0 521 113" 
        fill="none"
        preserveAspectRatio="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <defs>
          {/* Normal gradient */}
          <linearGradient id={gradientId} x1="30" y1="30" x2="39.9945" y2="124.948" gradientUnits="userSpaceOnUse">
            <stop offset="1" stopColor="#2A2D35"/>
            <stop stopColor="#262930"/>
          </linearGradient>
          
          {/* Hover gradient */}
          <linearGradient id={hoverGradientId} x1="30" y1="54" x2="486" y2="54" gradientUnits="userSpaceOnUse">
            <stop offset="0" stopColor="#9404C3"/>
            <stop offset="1" stopColor="#47025D"/>
          </linearGradient>
          
          {/* Normal state filter */}
          <filter id={filterId} x="0" y="0" width="521" height="113" filterUnits="userSpaceOnUse" colorInterpolationFilters="sRGB">
            <feFlood floodOpacity="0" result="BackgroundImageFix"/>
            <feColorMatrix in="SourceAlpha" type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0" result="hardAlpha"/>
            <feOffset dx="10" dy="10"/>
            <feGaussianBlur stdDeviation="12.5"/>
            <feColorMatrix type="matrix" values="0 0 0 0 0.109804 0 0 0 0 0.117647 0 0 0 0 0.141176 0 0 0 0.9 0"/>
            <feBlend mode="normal" in2="BackgroundImageFix" result="effect1_dropShadow_570_233"/>
            <feColorMatrix in="SourceAlpha" type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0" result="hardAlpha"/>
            <feOffset dx="-10" dy="-10"/>
            <feGaussianBlur stdDeviation="10"/>
            <feColorMatrix type="matrix" values="0 0 0 0 0.203922 0 0 0 0 0.219608 0 0 0 0 0.258824 0 0 0 0.9 0"/>
            <feBlend mode="normal" in2="effect1_dropShadow_570_233" result="effect2_dropShadow_570_233"/>
            <feColorMatrix in="SourceAlpha" type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0" result="hardAlpha"/>
            <feOffset dx="10" dy="-10"/>
            <feGaussianBlur stdDeviation="10"/>
            <feColorMatrix type="matrix" values="0 0 0 0 0.109804 0 0 0 0 0.117647 0 0 0 0 0.141176 0 0 0 0.2 0"/>
            <feBlend mode="normal" in2="effect2_dropShadow_570_233" result="effect3_dropShadow_570_233"/>
            <feColorMatrix in="SourceAlpha" type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0" result="hardAlpha"/>
            <feOffset dx="-10" dy="10"/>
            <feGaussianBlur stdDeviation="10"/>
            <feColorMatrix type="matrix" values="0 0 0 0 0.109804 0 0 0 0 0.117647 0 0 0 0 0.141176 0 0 0 0.2 0"/>
            <feBlend mode="normal" in2="effect3_dropShadow_570_233" result="effect4_dropShadow_570_233"/>
            <feBlend mode="normal" in="SourceGraphic" in2="effect4_dropShadow_570_233" result="shape"/>
            <feColorMatrix in="SourceAlpha" type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0" result="hardAlpha"/>
            <feOffset dx="-1" dy="-1"/>
            <feGaussianBlur stdDeviation="1"/>
            <feComposite in2="hardAlpha" operator="arithmetic" k2="-1" k3="1"/>
            <feColorMatrix type="matrix" values="0 0 0 0 0.109804 0 0 0 0 0.117647 0 0 0 0 0.141176 0 0 0 0.5 0"/>
            <feBlend mode="normal" in2="shape" result="effect5_innerShadow_570_233"/>
            <feColorMatrix in="SourceAlpha" type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0" result="hardAlpha"/>
            <feOffset dx="1" dy="1"/>
            <feGaussianBlur stdDeviation="1"/>
            <feComposite in2="hardAlpha" operator="arithmetic" k2="-1" k3="1"/>
            <feColorMatrix type="matrix" values="0 0 0 0 0.203922 0 0 0 0 0.219608 0 0 0 0 0.258824 0 0 0 0.3 0"/>
            <feBlend mode="normal" in2="effect5_innerShadow_570_233" result="effect6_innerShadow_570_233"/>
          </filter>
          
          {/* Hover state filter - with the specified drop shadow */}
          <filter id={`${filterId}_hover`} x="0" y="0" width="521" height="113" filterUnits="userSpaceOnUse" colorInterpolationFilters="sRGB">
            {/* 10px 10px 25px rgba(28, 30, 36, 0.90) */}
            <feColorMatrix in="SourceAlpha" type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0" result="hardAlpha1"/>
            <feOffset dx="10" dy="10"/>
            <feGaussianBlur stdDeviation="8.33"/>
            <feColorMatrix type="matrix" values="0 0 0 0 0.109804 0 0 0 0 0.117647 0 0 0 0 0.141176 0 0 0 0.9 0"/>
            <feBlend mode="normal" in2="SourceGraphic" result="effect1_dropShadow"/>
            
            {/* -10px -10px 20px rgba(52, 56, 66, 0.90) */}
            <feColorMatrix in="SourceAlpha" type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0" result="hardAlpha2"/>
            <feOffset dx="-10" dy="-10"/>
            <feGaussianBlur stdDeviation="6.67"/>
            <feColorMatrix type="matrix" values="0 0 0 0 0.203922 0 0 0 0 0.219608 0 0 0 0 0.258824 0 0 0 0.9 0"/>
            <feBlend mode="normal" in2="effect1_dropShadow" result="effect2_dropShadow"/>
            
            {/* 10px -10px 20px rgba(28, 30, 36, 0.20) */}
            <feColorMatrix in="SourceAlpha" type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0" result="hardAlpha3"/>
            <feOffset dx="10" dy="-10"/>
            <feGaussianBlur stdDeviation="6.67"/>
            <feColorMatrix type="matrix" values="0 0 0 0 0.109804 0 0 0 0 0.117647 0 0 0 0 0.141176 0 0 0 0.2 0"/>
            <feBlend mode="normal" in2="effect2_dropShadow" result="effect3_dropShadow"/>
            
            {/* -10px 10px 20px rgba(28, 30, 36, 0.20) */}
            <feColorMatrix in="SourceAlpha" type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0" result="hardAlpha4"/>
            <feOffset dx="-10" dy="10"/>
            <feGaussianBlur stdDeviation="6.67"/>
            <feColorMatrix type="matrix" values="0 0 0 0 0.109804 0 0 0 0 0.117647 0 0 0 0 0.141176 0 0 0 0.2 0"/>
            <feBlend mode="normal" in2="effect3_dropShadow" result="effect4_dropShadow"/>
            <feBlend mode="normal" in="SourceGraphic" in2="effect4_dropShadow" result="shape"/>
            
            {/* Inner shadow: 1px 1px 2px 0px rgba(52, 56, 66, 0.30) inset */}
            <feColorMatrix in="SourceAlpha" type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0" result="hardAlpha5"/>
            <feOffset dx="1" dy="1"/>
            <feGaussianBlur stdDeviation="0.67"/>
            <feComposite in2="hardAlpha5" operator="arithmetic" k2="-1" k3="1"/>
            <feColorMatrix type="matrix" values="0 0 0 0 0.203922 0 0 0 0 0.219608 0 0 0 0 0.258824 0 0 0 0.3 0"/>
            <feBlend mode="normal" in2="shape" result="effect5_innerShadow"/>
            
            {/* Inner shadow: -1px -1px 2px 0px rgba(28, 30, 36, 0.50) inset */}
            <feColorMatrix in="SourceAlpha" type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0" result="hardAlpha6"/>
            <feOffset dx="-1" dy="-1"/>
            <feGaussianBlur stdDeviation="0.67"/>
            <feComposite in2="hardAlpha6" operator="arithmetic" k2="-1" k3="1"/>
            <feColorMatrix type="matrix" values="0 0 0 0 0.109804 0 0 0 0 0.117647 0 0 0 0 0.141176 0 0 0 0.5 0"/>
            <feBlend mode="normal" in2="effect5_innerShadow" result="effect6_innerShadow"/>
          </filter>
        </defs>
        
        {/* Button path */}
        <g filter={isHovered ? `url(#${filterId}_hover)` : `url(#${filterId})`}>
          <path 
            d="M41.1362 76.4884L30.3953 57.6917C30.1362 57.2384 30 56.7254 30 56.2033V33C30 31.3431 31.3432 30 33 30H472.259C473.336 30 474.33 30.5769 474.864 31.5116L485.605 50.3083C485.864 50.7616 486 51.2746 486 51.7967V75C486 76.6569 484.657 78 483 78H43.741C42.6644 78 41.6704 77.4231 41.1362 76.4884Z" 
            fill={isHovered ? `url(#${hoverGradientId})` : `url(#${gradientId})`}
          />
        </g>
      </svg>
      
      {/* Actual button */}
      <button
        type={type}
        disabled={disabled || isLoading}
        onClick={onClick}
        className={`neumorphic-button ${isLoading ? 'neumorphic-button-loading' : ''}`}
        style={{
          position: 'absolute',
          width: '100%',
          height: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: 'transparent',
          color: isHovered ? 'white' : textColor, // White when hovered, customizable otherwise
          border: 'none',
          outline: 'none',
          cursor: disabled || isLoading ? 'not-allowed' : 'pointer',
          fontSize: 'clamp(10px, 1.8vw, 15px)',
          fontFamily: 'Poppins',
          zIndex: 2,
          opacity: disabled ? 0.7 : 1,
          transition: 'all 0.2s ease',
          padding: 0 // Remove default padding
        }}
      >
        {isLoading ? (
          <div className="spinner-container">
            <div className="spinner"></div>
          </div>
        ) : (
          children
        )}
      </button>
      
      {/* CSS for the loading spinner */}
      <style>
        {`
          .neumorphic-button-loading {
            color: transparent !important;
            pointer-events: none;
          }
          
          .spinner-container {
            position: absolute;
            top: 0;
            left: 0;
            width: 100%;
            height: 100%;
            display: flex;
            align-items: center;
            justify-content: center;
          }
          
          .spinner {
            width: 1.5rem;
            height: 1.5rem;
            border-radius: 50%;
            border: 2px solid rgba(255, 255, 255, 0.2);
            border-top-color: white;
            animation: spinner-rotation 0.8s linear infinite;
          }
          
          @keyframes spinner-rotation {
            to {
              transform: rotate(360deg);
            }
          }
        `}
      </style>
    </div>
  );
};

const arePropsEqual = (prevProps, nextProps) => {
  return (
    prevProps.children === nextProps.children &&
    prevProps.type === nextProps.type &&
    prevProps.onClick === nextProps.onClick &&
    prevProps.disabled === nextProps.disabled &&
    prevProps.isLoading === nextProps.isLoading &&
    prevProps.className === nextProps.className &&
    prevProps.textColor === nextProps.textColor &&
    JSON.stringify(prevProps.style) === JSON.stringify(nextProps.style)
  );
};

NeumorphicButtonLong.propTypes = {
  children: PropTypes.node,
  type: PropTypes.string,
  onClick: PropTypes.func,
  disabled: PropTypes.bool,
  isLoading: PropTypes.bool,
  className: PropTypes.string,
  textColor: PropTypes.string,
  style: PropTypes.object
};

NeumorphicButtonLong.defaultProps = {
  type: 'button',
  className: '',
  textColor: '#9C9DA7',
  style: {}
};

NeumorphicButtonLong.displayName = 'NeumorphicButtonLong';

export default memo(NeumorphicButtonLong, arePropsEqual);